/**
 * MapLibre map of candidate and selected assets.
 *
 * Accessibility (PRD 10.3): the map is a *view*, never the only way to reach a
 * site. Every marker is mirrored in the ranked list, which is keyboard
 * navigable, and selection is two-way. Markers encode state with fill, stroke
 * and a numeral, so colour is not the only signal.
 */

import { useEffect, useMemo, useRef } from 'react'
import maplibregl, { type Map as MapLibreMap, type Marker } from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'

export interface MapAsset {
  id: string
  name: string
  latitude: number
  longitude: number
  rank?: number | null
  selected?: boolean
  score?: number | null
  mediaType?: string | null
  locality?: string | null
  available?: boolean | null
  synthetic?: boolean
}

/**
 * A raster style built from OpenStreetMap tiles.
 *
 * Deliberately not a vector style behind an API key: the PRD leaves the map
 * provider and its commercial licence as an open decision (section 20), so the
 * POC ships something that works with no key and is trivial to swap. Replace
 * `MAP_STYLE` with the licensed provider's style URL when that is decided.
 */
const MAP_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      maxzoom: 19,
      attribution: '© OpenStreetMap contributors',
    },
  },
  layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
}

function markerElement(asset: MapAsset, isFocused: boolean): HTMLElement {
  const el = document.createElement('button')
  el.type = 'button'
  el.setAttribute(
    'aria-label',
    `${asset.name}${asset.locality ? `, ${asset.locality}` : ''}` +
      (asset.selected ? ', in the selected bundle' : '') +
      (asset.score !== null && asset.score !== undefined
        ? `, suitability ${asset.score.toFixed(0)}`
        : ''),
  )
  const size = asset.selected ? 26 : 20
  const fill = asset.selected
    ? 'var(--accent)'
    : asset.available === false
      ? 'var(--bg-sunken)'
      : 'var(--bg-raised)'
  const border = asset.selected
    ? 'var(--accent)'
    : asset.available === false
      ? 'var(--danger)'
      : 'var(--border-strong)'
  const color = asset.selected ? '#fff' : 'var(--text)'

  Object.assign(el.style, {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '50%',
    background: fill,
    border: `2px solid ${border}`,
    color,
    font: '600 11px var(--sans)',
    display: 'grid',
    placeItems: 'center',
    cursor: 'pointer',
    padding: '0',
    boxShadow: isFocused
      ? '0 0 0 3px var(--accent), 0 2px 6px rgb(0 0 0 / 25%)'
      : '0 1px 4px rgb(0 0 0 / 25%)',
    // A dashed outline marks an occupied asset without relying on hue.
    borderStyle: asset.available === false ? 'dashed' : 'solid',
  })
  el.textContent = asset.rank ? String(asset.rank) : ''
  return el
}

export function AssetMap({
  assets,
  focusedId,
  onSelect,
  bufferRadiusM,
}: {
  assets: MapAsset[]
  focusedId?: string | null
  onSelect?: (id: string) => void
  bufferRadiusM?: number
}) {
  const container = useRef<HTMLDivElement>(null)
  const map = useRef<MapLibreMap | null>(null)
  const markers = useRef<Map<string, Marker>>(new Map())
  const fitted = useRef(false)

  const points = useMemo(
    () =>
      assets.filter(
        (a) => Number.isFinite(a.latitude) && Number.isFinite(a.longitude),
      ),
    [assets],
  )

  useEffect(() => {
    if (!container.current || map.current) return
    map.current = new maplibregl.Map({
      container: container.current,
      style: MAP_STYLE,
      center: [72.87, 19.07],
      zoom: 10,
      attributionControl: { compact: true },
    })
    map.current.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')
    map.current.addControl(new maplibregl.ScaleControl({ maxWidth: 90, unit: 'metric' }))

    // MapLibre sizes its canvas once, at construction. Any later change to the
    // container - a grid reflow, a drawer opening, a window resize - leaves the
    // canvas smaller than its box, showing dead space beside the map. Watching
    // the container and calling resize() is the fix.
    const observer = new ResizeObserver(() => map.current?.resize())
    observer.observe(container.current)

    return () => {
      observer.disconnect()
      map.current?.remove()
      map.current = null
      markers.current.clear()
    }
  }, [])

  // Redraw markers whenever the set or the selection changes.
  useEffect(() => {
    const instance = map.current
    if (!instance) return

    for (const [id, marker] of markers.current) {
      if (!points.some((p) => p.id === id)) {
        marker.remove()
        markers.current.delete(id)
      }
    }

    for (const asset of points) {
      markers.current.get(asset.id)?.remove()
      const marker = new maplibregl.Marker({
        element: markerElement(asset, asset.id === focusedId),
      })
        .setLngLat([asset.longitude, asset.latitude])
        .setPopup(
          new maplibregl.Popup({ offset: 14, closeButton: false }).setHTML(
            `<strong>${escapeHtml(asset.name)}</strong><br/>` +
              `${escapeHtml(asset.mediaType ?? '')}` +
              (asset.locality ? ` · ${escapeHtml(asset.locality)}` : '') +
              (asset.score !== null && asset.score !== undefined
                ? `<br/>Suitability ${asset.score.toFixed(1)}`
                : '') +
              (asset.available === false ? '<br/><em>Occupied in this window</em>' : '') +
              (asset.synthetic ? '<br/><em>Sample data</em>' : ''),
          ),
        )
        .addTo(instance)
      marker.getElement().addEventListener('click', () => onSelect?.(asset.id))
      markers.current.set(asset.id, marker)
    }

    // Fit once per asset set, not on every selection change, so the map does
    // not jump while the planner is clicking through the list.
    if (points.length && !fitted.current) {
      const bounds = new maplibregl.LngLatBounds()
      points.forEach((p) => bounds.extend([p.longitude, p.latitude]))
      instance.fitBounds(bounds, { padding: 56, maxZoom: 14, duration: 0 })
      fitted.current = true
    }
  }, [points, focusedId, onSelect])

  // A new search resets the fit.
  useEffect(() => {
    fitted.current = false
  }, [points.length])

  // Pan to the focused asset without changing zoom.
  useEffect(() => {
    if (!focusedId || !map.current) return
    const asset = points.find((p) => p.id === focusedId)
    if (asset) {
      map.current.easeTo({ center: [asset.longitude, asset.latitude], duration: 400 })
    }
  }, [focusedId, points])

  return (
    <div className="map-shell">
      <div className="map" ref={container} role="application" aria-label="Asset map" />
      <div className="map-legend" aria-hidden>
        <div className="row">
          <span className="swatch" style={{ background: 'var(--accent)' }} />
          In the bundle
        </div>
        <div className="row">
          <span className="swatch" style={{ background: 'var(--bg-raised)', borderColor: 'var(--border-strong)' }} />
          Ranked candidate
        </div>
        <div className="row">
          <span
            className="swatch"
            style={{ background: 'var(--bg-sunken)', borderColor: 'var(--danger)', borderStyle: 'dashed' }}
          />
          Occupied
        </div>
        {bufferRadiusM ? <div className="faint">Metrics use a {bufferRadiusM} m buffer</div> : null}
      </div>
    </div>
  )
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c,
  )
}
